import { makeDialog } from '@cast/design-system';
import { getUser } from '@cast/infrastructure-auth';
import {
  useRbacRoleBindings,
  useRbacRolesQuery,
} from '@cast/shared-rbac-data-access';
import { Organization } from '@cast/types';
import { Member } from '@cast/features-organization-shared-types';

import { useDeleteOrganizationDialog } from '../useDeleteOrganizationDialog';
import {
  RemoveUserFromOrganizationDialogContent,
  RemoveUserFromOrganizationDialogProps,
} from './RemoveUserFromOrganizationDialog';
import { wouldRemoveLastOwner } from '../../utils/rbac-validation';

const useRemoveUserFromOrganizationDialogBase =
  makeDialog<RemoveUserFromOrganizationDialogProps>(
    RemoveUserFromOrganizationDialogContent
  );

type OpenRemoveUserFromOrganizationDialogArgs = {
  organization: Organization;
  members: Member[];
  onSuccessfulRemove?: () => void;
  isMe?: boolean;
};

export const useRemoveUserFromOrganizationDialog = () => {
  const { openDialog } = useRemoveUserFromOrganizationDialogBase();
  const { roleBindings } = useRbacRoleBindings({ enabled: true });
  const { data: roles = [] } = useRbacRolesQuery({ type: 'DEFAULT' });
  const { openModal: openDeleteDialog } = useDeleteOrganizationDialog();

  const openModal = ({
    organization,
    members,
    onSuccessfulRemove,
    isMe = false,
  }: OpenRemoveUserFromOrganizationDialogArgs) => {
    let cannotLeave = false;
    if (isMe && roleBindings && roles.length > 0) {
      const currentUser = getUser();
      if (currentUser?.guid) {
        cannotLeave = wouldRemoveLastOwner({
          roleBindings,
          roles,
          targetUserId: currentUser.guid,
          organizationScopeId: organization.id,
        });
      }
    }

    openDialog({
      organization,
      members,
      isMe,
      cannotLeave,
      onSuccess: onSuccessfulRemove,
      onDeleteOrganization: cannotLeave
        ? () => openDeleteDialog(organization)
        : undefined,
    });
  };

  return { openModal };
};
